// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import firebase from "firebase";

import errorHandler from "../util/errorHandler";
import { auth, setFirebasePersistenceNone } from "./firebase";

const loginWithPassword = async ({ email, password }) => {
  try {
    await auth.signInWithEmailAndPassword(email, password);
  } catch (e) {
    if (!["auth/wrong-password", "auth/user-not-found"].includes(e.code)) {
      errorHandler.report(new Error(`${e.code}: ${e.message}`));
    }
    throw e;
  }
};

const handleOAuthLoginError = (error) => {
  if (
    ![
      "auth/popup-closed-by-user",
      "auth/account-exists-with-different-credential",
      "auth/popup-blocked",
    ].includes(error.code)
  ) {
    errorHandler.report(new Error(`${error.code}: ${error.message}`));
    return error;
  }
  throw error;
};

const oAuthLoginWithPopup = async (provider) => {
  try {
    await auth.signInWithPopup(provider);
  } catch (e) {
    handleOAuthLoginError(e);
  }
};

const oAuthLoginWithRedirect = async (provider) => {
  try {
    await auth.signInWithRedirect(provider);
  } catch (e) {
    handleOAuthLoginError(e);
  }
};

const configureAppleOAuthProvider = () => {
  const provider = new firebase.auth.OAuthProvider("apple.com");
  provider.setCustomParameters({
    locale: "pt_BR",
  });
  return provider;
};

const oAuthLogin = async ({ loginSource, provider }) =>
  loginSource === "flutter"
    ? oAuthLoginWithRedirect(provider)
    : oAuthLoginWithPopup(provider);

const loginWithCustomToken = async (token) => {
  try {
    await auth.signInWithCustomToken(token);
  } catch (e) {
    errorHandler.report(new Error(`${e.code}: ${e.message}`));
    throw e;
  }
};

export const login = async ({ loginProvider, credentials, loginSource }) => {
  await setFirebasePersistenceNone();

  if (loginProvider === "password") {
    await loginWithPassword({
      email: credentials.email,
      password: credentials.password,
    });
  }

  if (loginProvider === "google") {
    const provider = new firebase.auth.GoogleAuthProvider();
    await oAuthLogin({ loginSource, provider });
  }

  if (loginProvider === "apple") {
    const provider = configureAppleOAuthProvider();
    await oAuthLogin({ loginSource, provider });
  }

  if (loginProvider === "custom") {
    await loginWithCustomToken(credentials.token);
  }
};
